import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowLeft, Mail, FileText } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const SECTIONS = [
  {
    n: 1,
    id: "request",
    title: "Vehicle Request Policy",
    body: [
      "Customers may submit a request specifying:",
      ["Make and model", "Year range", "Maximum budget", "Exterior and interior color", "Mileage preference", "Title preference", "Vehicle condition", "Other specific requirements"],
      "Submitting a request does not guarantee that GETEASY CAR will find or purchase a vehicle that meets every requested specification.",
    ],
  },
  {
    n: 2,
    id: "selection",
    title: "Vehicle Selection & Auction Policy",
    body: [
      "GETEASY CAR may search authorized vehicle auctions and other approved sources to locate a suitable vehicle.",
      "Before purchasing, GETEASY CAR will provide available information about the vehicle, including when available:",
      ["VIN", "Auction photos", "Mileage", "Title status", "Known damage", "Vehicle history information", "Estimated repair requirements", "Estimated total cost"],
      "Customers are responsible for reviewing and approving the vehicle before purchase.",
      "Auction descriptions, photographs, condition reports, and vehicle history information are provided by third parties and may not identify every existing or potential defect.",
    ],
  },
  {
    n: 3,
    id: "approval",
    title: "Customer Approval",
    body: [
      "GETEASY CAR will obtain customer approval before purchasing a vehicle on the customer's behalf.",
      "Once the customer approves a vehicle and the purchase is completed, the transaction may become non-refundable, subject to the applicable auction, seller, transportation, and other third-party policies.",
    ],
  },
  {
    n: 4,
    id: "payment",
    title: "Payment Policy",
    body: [
      "Customers must pay the required amount or deposit before GETEASY CAR proceeds with a vehicle purchase or other services.",
      "The customer's total cost may include:",
      ["Vehicle purchase price", "Auction fees", "GETEASY CAR service fees", "Transportation", "Taxes and applicable government fees", "Repair costs", "Parts and labor", "Other agreed-upon expenses"],
      "Additional costs that become necessary because of unexpected vehicle conditions will be communicated to the customer for approval whenever reasonably possible.",
    ],
  },
  {
    n: 5,
    id: "repair",
    title: "Repair Policy",
    body: [
      "When repair services are requested, GETEASY CAR may coordinate repairs through qualified repair facilities or technicians.",
      "Repair estimates are based on information available at the time of inspection. Hidden damage or additional mechanical problems may be discovered after disassembly or during repairs.",
      "Additional repairs requiring significant additional cost will require customer approval before proceeding.",
    ],
  },
  {
    n: 6,
    id: "delivery",
    title: "Delivery Policy",
    body: [
      "After the vehicle has been purchased and any agreed repairs have been completed, GETEASY CAR will arrange delivery to the customer's agreed location.",
      "Delivery times are estimates and may be affected by:",
      ["Transportation availability", "Weather", "Auction delays", "Mechanical issues", "Repair delays", "Government or title processing", "Other circumstances outside GETEASY CAR's control"],
    ],
  },
  {
    n: 7,
    id: "condition",
    title: "Vehicle Condition",
    body: [
      "GETEASY CAR makes reasonable efforts to provide customers with available information about a vehicle before purchase.",
      "However, GETEASY CAR does not guarantee that a used, auction, salvage, rebuilt, or previously damaged vehicle will be free from mechanical, electrical, structural, cosmetic, or other defects.",
      "Customers should carefully review all available vehicle information before approving a purchase.",
    ],
  },
  {
    n: 8,
    id: "title",
    title: "Title Policy",
    body: [
      "The vehicle's title status will be disclosed based on available documentation.",
      "Depending on the vehicle, the title may be:",
      ["Clean", "Salvage", "Rebuilt", "Reconstructed", "Other title status permitted by applicable law"],
      "Customers are responsible for confirming that the vehicle's title status meets their intended use, financing, insurance, and registration requirements.",
    ],
  },
  {
    n: 9,
    id: "cancellation",
    title: "Cancellation Policy",
    body: [
      "A customer may request cancellation before GETEASY CAR purchases a vehicle.",
      "Once a vehicle has been purchased, cancellation may be subject to non-refundable auction fees, transportation costs, deposits, service fees, repair costs, and other expenses already incurred.",
      "Any refund will be determined after deducting applicable non-refundable expenses.",
    ],
  },
  {
    n: 10,
    id: "refund",
    title: "Refund Policy",
    body: [
      "Refunds are not guaranteed after a vehicle purchase has been completed.",
      "Amounts already paid to third parties, including auctions, transportation companies, repair facilities, government agencies, or other service providers, may be non-refundable.",
      "GETEASY CAR will provide the customer with an explanation of applicable deductions when a refund is requested.",
    ],
  },
  {
    n: 11,
    id: "responsibility",
    title: "Customer Responsibility",
    body: [
      "Customers are responsible for providing accurate information, including:",
      ["Contact information", "Vehicle preferences", "Budget", "Delivery address", "Payment information", "Title requirements", "Other information necessary to complete the transaction"],
      "GETEASY CAR is not responsible for delays or additional costs caused by inaccurate or incomplete information provided by the customer.",
    ],
  },
  {
    n: 12,
    id: "status",
    title: "Request Status",
    body: [
      "Customers may track their request through the GETEASY CAR website.",
      "Requests may display the following statuses:",
      [
        "Open — Request has been submitted and is awaiting review.",
        "In Progress — GETEASY CAR is actively working on the request.",
        "Delivery — The vehicle has been purchased and is being prepared or transported to the customer.",
        "Closed — The request has been completed or closed.",
      ],
      "If a request is closed without completion, GETEASY CAR may provide the reason for closure when appropriate.",
    ],
  },
  {
    n: 13,
    id: "privacy",
    title: "Privacy Policy",
    body: [
      "GETEASY CAR may collect information necessary to process customer requests, including name, contact information, vehicle preferences, payment information, and delivery information.",
      "Customer information will be used to provide requested services, communicate with customers, process transactions, arrange transportation, and comply with applicable legal requirements.",
      "GETEASY CAR will not sell customer personal information to third parties.",
    ],
  },
  {
    n: 14,
    id: "liability",
    title: "Limitation of Liability",
    body: [
      "GETEASY CAR is not responsible for delays, losses, or additional costs caused by circumstances outside its reasonable control, including auction delays, transportation delays, weather, mechanical problems, title processing delays, government actions, or third-party service providers.",
      "Nothing in these policies is intended to waive any rights or protections that cannot legally be waived under applicable law.",
    ],
  },
  {
    n: 15,
    id: "changes",
    title: "Policy Changes",
    body: [
      "GETEASY CAR may update these policies from time to time. Updated policies will be posted on the website with the applicable effective date.",
    ],
  },
  {
    n: 16,
    id: "agreement",
    title: "Customer Agreement",
    body: [
      "By submitting a vehicle request or authorizing GETEASY CAR to proceed with a purchase or service, the customer acknowledges that they have reviewed and agreed to these policies.",
    ],
  },
  {
    n: 17,
    id: "law",
    title: "Governing Law",
    body: [
      "These policies and any transaction or service provided by GETEASY CAR will be governed by the laws of the state in which GETEASY CAR's principal business office is located, without regard to conflict-of-law principles, except to the extent that applicable law requires otherwise.",
    ],
  },
  {
    n: 18,
    id: "disputes",
    title: "Dispute Resolution",
    body: [
      "Customers should first contact GETEASY CAR to attempt to resolve any complaint, dispute, or claim informally. If the matter cannot be resolved informally, the parties may pursue any remedies available under applicable law. Nothing in these policies prevents a customer from filing a complaint with a government agency or exercising any non-waivable legal right.",
      "To the extent permitted by applicable law, the parties may agree in writing to resolve a dispute through mediation or arbitration. Any arbitration agreement must be separately confirmed by the parties and will identify the applicable rules, location, costs, and procedures.",
    ],
  },
  {
    n: 19,
    id: "venue",
    title: "Venue",
    body: [
      "To the extent permitted by applicable law, any court proceeding relating to these policies or GETEASY CAR's services will be brought in the state or federal courts located in the county and state where GETEASY CAR's principal business office is located. Each party consents to the jurisdiction of those courts, subject to any rights that cannot legally be waived.",
    ],
  },
  {
    n: 20,
    id: "notices",
    title: "Notices",
    body: [
      "GETEASY CAR may provide notices to customers by email, text message, telephone, postal mail, through the GETEASY CAR website, or by another reasonable method using the contact information provided by the customer.",
      "Customers are responsible for keeping their contact information current. A notice sent to the customer's most recently provided email or mailing address will be considered delivered when sent or deposited, unless GETEASY CAR receives a delivery failure notice.",
    ],
  },
  {
    n: 21,
    id: "complaints",
    title: "Complaints and Claims",
    body: [
      "Customers may contact GETEASY CAR about a complaint, refund request, service issue, or other claim by using the contact information provided on the GETEASY CAR website or by replying to a GETEASY CAR communication related to the customer's request.",
      "A complaint or claim should include the customer's name, contact information, request or transaction number if available, a description of the issue, relevant dates, and supporting documents or photographs. GETEASY CAR will review the matter and may request additional information.",
      "Customers should submit complaints or claims as soon as reasonably possible after the issue arises. Contacting GETEASY CAR does not waive any legal rights or deadlines that may apply under applicable law.",
    ],
  },
];

const Section = ({ n, id, title, body }) => (
  <section
    id={id}
    data-testid={`policy-section-${n}`}
    className="scroll-mt-24 bg-[#18181E] border border-white/10 rounded-2xl p-6 sm:p-8"
  >
    <div className="flex items-start gap-4">
      <div className="shrink-0 w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center font-mono-brand text-amber-400 text-sm font-semibold">
        {String(n).padStart(2, "0")}
      </div>
      <div className="flex-1">
        <h2 className="font-display text-xl sm:text-2xl font-bold uppercase tracking-tight">
          {title}
        </h2>
        <div className="mt-4 space-y-3 text-sm sm:text-[15px] text-slate-300 leading-relaxed">
          {body.map((item, i) =>
            Array.isArray(item) ? (
              <ul key={i} className="pl-5 space-y-1.5 list-disc marker:text-amber-500/70">
                {item.map((li, j) => (
                  <li key={j}>{li}</li>
                ))}
              </ul>
            ) : (
              <p key={i}>{item}</p>
            )
          )}
        </div>
      </div>
    </div>
  </section>
);

const Policies = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-slate-100">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <Link
          to="/"
          data-testid="policies-back-btn"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-400 text-sm mb-6 transition"
        >
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>

        <div className="mb-10">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-amber-400">
            <FileText className="w-3.5 h-3.5" /> / Legal
          </div>
          <h1
            className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight"
            data-testid="policies-title"
          >
            Customer Policies & <span className="gold-text">Terms of Service</span>
          </h1>
          <p className="mt-4 text-slate-400 font-mono-brand text-xs uppercase tracking-widest">
            Effective Date: October 5, 2026
          </p>
          <p className="mt-6 text-slate-300 leading-relaxed max-w-3xl">
            GETEASY CAR helps customers locate, purchase, repair, and deliver vehicles based
            on their requested specifications and budget. By submitting a vehicle request,
            the customer agrees to the following policies.
          </p>

          <a
            href="mailto:geteasycar26@gmail.com"
            data-testid="policies-contact-email"
            className="mt-6 inline-flex items-center gap-2 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-300 font-semibold px-4 py-2.5 rounded-xl text-sm transition"
          >
            <Mail className="w-4 h-4" /> geteasycar26@gmail.com
          </a>
        </div>

        {/* Table of contents */}
        <div
          className="bg-gradient-to-b from-[#18181E] to-[#121216] border border-white/10 rounded-2xl p-6 mb-10"
          data-testid="policies-toc"
        >
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-amber-400 mb-4">
            / Contents
          </div>
          <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="text-slate-300 hover:text-amber-400 transition"
                >
                  <span className="font-mono-brand text-amber-400/80 mr-2">
                    {String(s.n).padStart(2, "0")}
                  </span>
                  {s.title}
                </a>
              </li>
            ))}
          </ol>
        </div>

        <div className="space-y-5">
          {SECTIONS.map((s) => (
            <Section key={s.id} {...s} />
          ))}
        </div>

        <div className="mt-14 text-center">
          <div className="font-display text-sm uppercase tracking-widest text-slate-500">
            GETEASY CAR
          </div>
          <div className="font-mono-brand text-[11px] uppercase tracking-widest text-slate-600 mt-2">
            Vehicle Sourcing · Purchase · Repair Coordination · Delivery
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Policies;
